import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useState, useEffect } from "react";
import FileTableIcon from "@/components/files/fileTableIcon";
import { useTranslation } from "react-i18next";
import AppointmentTreeItemUploadButton from "@/components/appointmentDashboard/appointmentTreeItemUploadButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import useFileAction from "@/helper/customHooks/useFileActions";
import styles from "@/components/appointmentDashboard/appointmentTreeItemFileList.module.css";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface Props {
  appointment: any;
  handleSetNotification: (message: any, status: boolean) => void;
  handleReloadTrigger?: any;
}

const AppointmentTreeItemFileList: FC<Props> = ({
  appointment,
  handleSetNotification,
  handleReloadTrigger,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<string | false>("fileList");
  // const [notification, setNotification] = useState<string | null>(null);
  const { handleError, errorModal } = useErrorModal();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [documentsVisible, setDocumentsVisible] = useState<boolean>(false);
  const [hasInvisibleGeneratedFiles, setHasInvisibleGeneratedFiles] =
    useState<boolean>(false);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingFilename, setEditingFilename] = useState<string>("");
  const [editingFilenameError, setEditingFilenameError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "filename">({
      fieldOrder: [],
    });

  const selectedChildCompany: any = useSelector(
    (state: RootState) => state.parent,
  );
  const { editFileName, performFileAction } = useFileAction(
    handleError,
    selectedChildCompany?.subjectId,
  );
  const statusesToNotShowFileActionButtons = [
    "post_processing_required",
    "review_preparation_required",
    "completed",
    "canceled_not_in_time",
    "canceled",
    "missed",
    "declined",
  ];
  const showFileActionButtons =
    !!appointment &&
    !statusesToNotShowFileActionButtons.includes(appointment.status);

  const fetchVisibility = () => {
    try {
      const files = Object.values(appointment.file_set || {});
      if (!files || files.length === 0) {
        setDocumentsVisible(false);
        setHasInvisibleGeneratedFiles(false);
        return;
      }

      const anyVisible = files.some(
        (file: any) => file.visible_to_subject === true,
      );
      const hasInvisibleAndGenerated = files.some(
        (file: any) =>
          file.system_generated === true && file.visible_to_subject === false,
      );

      setDocumentsVisible(anyVisible);
      setHasInvisibleGeneratedFiles(hasInvisibleAndGenerated);
    } catch (error) {
      console.error("Failed to fetch visibility:", error);
    }
  };

  useEffect(() => {
    fetchVisibility();
  }, [appointment]);

  const handleStartEdit = (fileItem: any) => {
    setEditingFileId(fileItem.id || fileItem.view_link);
    setEditingFilename(fileItem.name);
    setEditingFilenameError(false);
  };

  const handleSaveEdit = (fileItem: any) => {
    if (!editingFilename.trim()) {
      setEditingFilenameError(true);
      focusFirstInvalidField({}, ["filename"]);
      return;
    }

    setEditingFilenameError(false);
    editFileName(
      { name: editingFilename.trim() },
      selectedChildCompany,
      fileItem,
      () => {
        if (handleReloadTrigger) {
          handleReloadTrigger();
        }
      },
    );
    setEditingFileId(null);
    setEditingFilename("");
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditingFilename("");
    setEditingFilenameError(false);
  };

  let appointmentFilesList: any = <></>;
  const renderAppointmentFilesList = () => {
    const renderedListItems = [];

    const hasNonSystemGeneratedFiles = appointment.file_set.some(
      (file: any) => file.system_generated !== true,
    );

    for (const fileItem of appointment.file_set) {
      const pretifiedFilename = fileItem.name.replace("_", " ");
      const isSystemGenerated = fileItem.system_generated === true;
      const fileIdentifier = fileItem.id || fileItem.view_link;
      const isEditing = editingFileId === fileIdentifier;
      const isLocked = fileItem.locked === true;

      renderedListItems.push(
        <ListItem
          key={fileItem.view_link}
          className={styles.listItem}
          data-testid={`appointment-file-${fileIdentifier}`}
        >
          <div className={styles.fileItemContainer}>
            <div className={styles.fileActions}>
              {hasNonSystemGeneratedFiles && (
                <Tooltip
                  arrow
                  disableInteractive
                  title={
                    isSystemGenerated ? t("cannot_rename_file") : t("rename")
                  }
                  placement="top"
                  enterDelay={2000}
                  enterNextDelay={500}
                >
                  <span>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStartEdit(fileItem);
                      }}
                      disabled={isSystemGenerated}
                      className={isSystemGenerated ? styles.disabledButton : ""}
                    >
                      <EditIcon fontSize="small" />
                    </GenericButton>
                  </span>
                </Tooltip>
              )}
              {fileItem.visible_to_subject === true && (
                <Tooltip
                  arrow
                  disableInteractive
                  title={t("not_visible_to_subject")}
                  placement="top"
                  enterDelay={2000}
                  enterNextDelay={500}
                >
                  <span>
                    <GenericButton
                      variant="icon"
                      color="iconBase"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleMarkSingleFileAsInvisible(fileItem);
                      }}
                    >
                      <VisibilityOffIcon color="error" fontSize="small" />
                    </GenericButton>
                  </span>
                </Tooltip>
              )}
              {fileItem.visible_to_subject === false && (
                <Tooltip
                  arrow
                  disableInteractive
                  title={t("delete")}
                  placement="top"
                  enterDelay={2000}
                  enterNextDelay={500}
                >
                  <span>
                    <GenericButton
                      variant="icon"
                      color="error"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleDeleteSingleFile(fileItem);
                      }}
                      disabled={isLocked}
                      className={isLocked ? styles.disabledButton : ""}
                    >
                      <DeleteIcon fontSize="small" />
                    </GenericButton>
                  </span>
                </Tooltip>
              )}
            </div>
            {isEditing ? (
              <>
                <input
                  ref={registerValidationTarget("filename")}
                  type="text"
                  value={editingFilename}
                  onChange={(e) => {
                    setEditingFilename(e.target.value);
                    setEditingFilenameError(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveEdit(fileItem);
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                  }}
                  className={styles.filenameInput}
                  aria-invalid={editingFilenameError}
                  autoFocus
                />
                {editingFilenameError && (
                  <Typography color="error" variant="caption" role="alert">
                    {t("field_required")}
                  </Typography>
                )}
                <div className={styles.editActions}>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("save")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      variant="icon"
                      color="secondary"
                      onClick={() => handleSaveEdit(fileItem)}
                      className={styles.editActionButton}
                    >
                      <CheckIcon fontSize="small" />
                    </GenericButton>
                  </Tooltip>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={t("cancel")}
                    placement="top"
                    enterDelay={2000}
                    enterNextDelay={500}
                  >
                    <GenericButton
                      variant="icon"
                      color="error"
                      onClick={handleCancelEdit}
                      className={styles.editActionButton}
                    >
                      <CancelIcon fontSize="small" />
                    </GenericButton>
                  </Tooltip>
                </div>
              </>
            ) : (
              <div className={styles.fileContent}>
                <FileTableIcon extension={fileItem.extension} />
                <a
                  className={styles.link}
                  href={"/api/v1" + fileItem.view_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {pretifiedFilename}
                </a>
              </div>
            )}
          </div>
        </ListItem>,
      );
    }

    if (renderedListItems.length > 0) {
      return renderedListItems;
    }

    return false;
  };
  if (appointment) {
    appointmentFilesList = renderAppointmentFilesList();
  }

  const handleExpand =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleMarkSingleFileAsInvisible = async (fileItem: any) => {
    await performFileAction(fileItem, "markAsInvisibleToSubject", () => {
      if (handleReloadTrigger) {
        handleReloadTrigger();
      }
    });
  };

  const handleToggleVisibility = async () => {
    const promise = Object.values(appointment.file_set).map(
      async (file: any) => {
        const response = await fetch(`/api/v1/file/${file.id}/`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visible_to_subject: false }),
        });
        if (!response.ok) {
          throw new Error("File visibility update failed");
        }
      },
    );

    try {
      await Promise.all(promise);
      handleSetNotification(t("set_to_invisible_success"), true);
      if (handleReloadTrigger) {
        handleReloadTrigger();
      }
    } catch (error: any) {
      handleError(error.message);
    }
  };

  const handleDeleteSingleFile = async (fileItem: any) => {
    const confirmed = await showConfirmationModalAsync(
      t("delete_item_question", { itemName: fileItem.name }),
    );
    if (!confirmed) return;

    try {
      const unlockResponse = await fetch(`/api/v1/file/${fileItem.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: false }),
      });
      if (!unlockResponse.ok) {
        throw new Error(`Unlocking failed for file ${fileItem.id}`);
      }

      const deleteResponse = await fetch(`/api/v1/file/${fileItem.id}/`, {
        method: "DELETE",
      });
      if (!deleteResponse.ok) {
        throw new Error("Delete operation failed");
      }

      handleSetNotification(t("delete_successful"), true);
      if (handleReloadTrigger) {
        handleReloadTrigger();
      }
    } catch (error: any) {
      handleError(error.message || error);
    }
  };

  const handleDeleteAllInvisibleGeneratedFiles = async () => {
    const generatedFiles = Object.values(appointment.file_set).filter(
      (file: any) =>
        file.system_generated === true && file.visible_to_subject === false,
    );

    const promises = generatedFiles.map(async (file: any) => {
      const unlockResponse = await fetch(`/api/v1/file/${file.id}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: false }),
      });
      if (!unlockResponse.ok) {
        throw new Error(`Unlocking failed for file ${file.id}`);
      }
      const deleteResponse = await fetch(`/api/v1/file/${file.id}/`, {
        method: "DELETE",
      });
      if (!deleteResponse.ok) {
        throw new Error("Delete operation failed");
      }
    });

    try {
      await Promise.all(promises);
      handleSetNotification(t("delete_successful"), true);
      if (handleReloadTrigger) {
        handleReloadTrigger();
      }
    } catch (error: any) {
      handleError(error.message);
    }
  };

  const accordionActionPopperStyle = {
    modifiers: [
      {
        name: "offset",
        options: { offset: [0, -7] },
      },
    ],
  };

  return (
    <>
      <div>
        <Accordion
          className={styles.accordion}
          expanded={expanded === "fileList"}
          onChange={handleExpand("fileList")}
          sx={{
            "&.MuiAccordion-root.Mui-expanded": {
              margin: "0",
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: "0",
            },
            "& .MuiAccordionSummary-root": {
              minHeight: "48px !important",
            },
            "& .MuiAccordionDetails-root": {
              padding: "0px 16px 16px 16px",
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={styles.accordionSummary}>
              <h2 className={styles.accordionTitle}>
                {t("documents_of_appointment")}
              </h2>
              {expanded === "fileList" && (
                <div className={styles.accordionActions}>
                  {showFileActionButtons &&
                    appointment &&
                    appointment.file_set &&
                    Object.keys(appointment.file_set).length > 0 && (
                      <>
                        {documentsVisible && (
                          <Tooltip
                            title={t(
                              "switch_all_documents_of_appointment_to_invisible",
                            )}
                            placement="top"
                            PopperProps={accordionActionPopperStyle}
                          >
                            <GenericButton
                              variant="icon"
                              onClick={async (event) => {
                                event.stopPropagation();
                                const confirmed =
                                  await showConfirmationModalAsync(
                                    t(
                                      "switch_all_documents_of_appointment_to_invisible_warning",
                                    ),
                                  );
                                if (confirmed) {
                                  handleToggleVisibility();
                                }
                              }}
                            >
                              <VisibilityOffIcon color="error" />
                            </GenericButton>
                          </Tooltip>
                        )}
                        {hasInvisibleGeneratedFiles && (
                          <Tooltip
                            title={t("delete_invisible_generated_documents")}
                            placement="top"
                            PopperProps={accordionActionPopperStyle}
                          >
                            <GenericButton
                              variant="icon"
                              onClick={async (event) => {
                                event.stopPropagation();
                                const confirmed =
                                  await showConfirmationModalAsync(
                                    t(
                                      "delete_invisible_generated_documents_warning",
                                    ),
                                  );
                                if (confirmed) {
                                  handleDeleteAllInvisibleGeneratedFiles();
                                }
                              }}
                            >
                              <DeleteIcon color="error" />
                            </GenericButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  {showFileActionButtons && (
                    <div
                      className={styles.uploadButton}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <AppointmentTreeItemUploadButton
                        useCase="documentWidget"
                        appointmentId={appointment?.id}
                        subject={appointment?.subject}
                        handleTriggerReload={handleReloadTrigger}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails data-testid="appointment-tree-item-file-list">
            {appointmentFilesList}
          </AccordionDetails>
        </Accordion>
      </div>

      {/* {notification && (
        <GenericNotification
          message={notification}
          variant="success"
          handleCloseSnackbar={() => setNotification(null)}
        />
      )} */}
      {errorModal}
    </>
  );
};

export default AppointmentTreeItemFileList;
